import React, { useState } from 'react';
import { useContext } from 'react';

import { Alert, notification } from 'antd';
import Axios from 'axios';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';
import { datePickerBeforeOnChange } from '@libs/NwMoment';

import { ListContext } from '@contexts/ListContext';
import { UserContext } from '@contexts/UserContext';

import { forceReloadCreditNote, useCreditNoteConfirm } from '@hooks/API/UseCreditNotes';

import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import { NWInput, NWDatePicker } from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';

const Wrapper = styled.div`
    input.read-only {
        background-color: ${(props) => props.theme.LayoutBodyBackground} !important;
    }
    .error-message-container {
        margin-top: 2rem;
    }
`;

const CreditNoteConfirmDrawer = ({ onClose, creditNoteID }) => {
    const [touchForm, setTouchForm] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const { mutateAsync: onCreditNoteConfirm } = useCreditNoteConfirm();

    const userContext = useContext(UserContext);
    const { currentUser } = userContext;

    const { cachedList } = useContext(ListContext);

    const accGroup = cachedList.accountingGroups.items.find(
        (accgroup) => accgroup.ID === currentUser.ActiveContext.accGroupId
    );
    const invoicenumbermethod = accGroup.ProgressiveNumberAlgorithm;
    const isNumberManual = invoicenumbermethod.indexOf('Manual') > -1;

    const initialValues = {
        Number: '',
        Date: '',
    };

    const saveInvoiceConfirmation = async (values, setSubmitting) => {
        setSubmitting(true);
        try {
            await onCreditNoteConfirm({
                data: {
                    CreditNoteID: creditNoteID,
                    Number: values.Number,
                    Date: values.Date,
                },
            });
            onClose();
        } catch (e) {
            setErrorMessage(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const getCreditNoteNumber = async (date, setFieldValue) => {
        try {
            const response = await Axios.get(`/ProgressiveNumber/Suggest?DateTime=${date}`);
            setFieldValue('Number', response);
        } catch (e) {
            notification.error({
                message: 'Error suggesting the credit note number',
                description: e?.message || 'Unknown error',
            });
        }
    };

    return (
        <NwDrawer touchForm={touchForm} onTouchForm={setTouchForm} onClose={onClose} title="Credit Note Confirmation">
            <Wrapper>
                <Formik
                    initialValues={initialValues}
                    onSubmit={(values, { setSubmitting }) => {
                        saveInvoiceConfirmation(values, setSubmitting);
                    }}
                    validationSchema={Yup.object().shape({
                        Number: Yup.string().required('A number/string is required'),
                        Date: Yup.string().required('A date is required'),
                    })}
                >
                    {({ isSubmitting, values, setFieldValue, handleSubmit }) => (
                        <NwForm values={values} onTouchForm={setTouchForm} onFinish={handleSubmit}>
                            <Field
                                component={NWDatePicker}
                                label="Credit Note Date"
                                name="Date"
                                format="MMM DD, YYYY"
                                getCalendarContainer={(triggerNode) => triggerNode.parentNode}
                                style={{ width: '100%' }}
                                value={values.Date ? getMoment(values.Date) : null}
                                manipulateValue={datePickerBeforeOnChange}
                                onAfterChange={(value) => {
                                    if (value) {
                                        getCreditNoteNumber(value, setFieldValue);
                                    }
                                }}
                            />
                            <Field
                                component={NWInput}
                                className={!isNumberManual ? 'read-only' : ''}
                                label="Credit Note Number"
                                name="Number"
                                maxLength={50}
                                type="text"
                                placeholder="input a date above to get a suggested credit note number"
                                readOnly={!isNumberManual}
                                value={values.Number}
                            />
                            <NwFormButtonsBar
                                marginTop="4rem"
                                left={
                                    <NwCancelButton
                                        disabled={isSubmitting}
                                        onClick={() => {
                                            setTouchForm(false);
                                            onClose();
                                        }}
                                    />
                                }
                                right={
                                    <NwSaveButton htmlType="submit" disabled={isSubmitting} loading={isSubmitting} />
                                }
                            />
                            <div className="error-message-container">
                                {errorMessage && <Alert message={errorMessage} type="error" showIcon />}
                            </div>
                        </NwForm>
                    )}
                </Formik>
            </Wrapper>
        </NwDrawer>
    );
};

export default CreditNoteConfirmDrawer;
